<template>
    <v-container>
        <div v-if="dataLoaded">
            <div :class="[rakenneElementit.length == 1 ? 'child ' : '', 'ho-collapsible']">
                <h3 class="heading" v-if="rakenneElementit.length == 1" :id="generateIdFromElementti(rakenneElementit[0])">
                    <a :name="'ho-goal-' + rakenneElementit[0].jarjestysnumero"></a>
                    <button :id="'ho-goal-' + rakenneElementit[0].jarjestysnumero"
                            class="ho-collapsible-trigger opened"
                            @click="toggleCollapse"
                            :aria-controls="'ho-goal-content-' + rakenneElementit[0].jarjestysnumero "
                            :aria-expanded="true"
                            role="button">
                        {{ localizedStringValue(rakenneElementit[0].nimi) }}
                        <font-awesome-icon :icon="['fas', 'chevron-down']" class="trigger-icon" aria-hidden="true" role="presentation" />
                    </button>
                </h3>
                <h2 class="heading" :id="generateIdFromElementti(rakenneElementit[0])" v-else>
                    <button :id="'ho-strategy-' + rakenneElementit[0].jarjestysnumero"
                            class="ho-collapsible-trigger opened"
                            @click="toggleCollapse"
                            :aria-controls="'ho-strategy-content-' + rakenneElementit[0].jarjestysnumero "
                            :aria-expanded="true"
                            role="button">
                        {{ localizedStringValue(rakenneElementit[0].nimi) }}
                        <font-awesome-icon :icon="['fas', 'chevron-down']" class="trigger-icon" aria-hidden="true" role="presentation" />
                    </button>
                </h2>
                <div :id="rakenneElementit.length > 1 ? 'ho-strategy-content-' + rakenneElementit[0].jarjestysnumero : 'ho-goal-content-' + rakenneElementit[0].jarjestysnumero"
                     class="ho-collapsible-content"
                     :aria-labelledby="rakenneElementit.length > 1 ? 'ho-strategy-' + rakenneElementit[0].jarjestysnumero : 'ho-goal-' + rakenneElementit[0].jarjestysnumero"
                     aria-hidden="false"
                     role="region">

                    <p class="structure-description" v-if="localizedStringValue(rakenneElementit[0].kuvaus)">{{ localizedStringValue(rakenneElementit[0].kuvaus) }}</p>

                    <ListTimeline v-show="listMode"
                                  :rakenne-elementit="rakenneElementit"
                                  :ministeriot="ministeriot"
                                  :kohde-linkit="kohdeLinkit"
                                  :kohteiden-linkit="kohteidenLinkit"
                                  :liittyvat-kohde-uuids="liittyvatKohdeUuids"
                                  :valitavoite-tyypit="valitavoiteTyypit"
                                  :json-api="jsonApi"/>
                    <Timeline v-show="!listMode"
                              :hallitus-uuid="configurationData.hallitusUuid"
                              :rakenne-elementit="rakenneElementit"
                              :ministeriot="ministeriot"
                              :kohde-linkit="kohdeLinkit"
                              :kohteiden-linkit="kohteidenLinkit"
                              :liittyvat-kohde-uuids="liittyvatKohdeUuids"
                              :valitavoite-tyypit="valitavoiteTyypit"
                              :json-api="jsonApi"
                              :image-path="imagePath"
                              />
                    <div class="timelineViewToggles">
                        <button type="button" class="btn btn-primary" :aria-label="t('change.to.list.view')" v-on:click="listMode = true">
                            <font-awesome-icon :icon="['fas', 'list']" class="trigger-icon" aria-hidden="true" role="presentation" />
                            <span aria-hidden="true" v-translate>list</span>
                        </button>
                        <button type="button" class="btn btn-primary" :aria-label="t('change.to.timeline.view')" v-on:click="listMode = false">
                            <font-awesome-icon :icon="['fas', 'stream']" class="trigger-icon" aria-hidden="true" role="presentation" />
                            <span aria-hidden="true" v-translate>timeline</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="!isValidConfiguration()">
            <Alert>
                <span v-translate>error.invalid.configuration</span>
            </Alert>
        </div>
        <div v-else-if="!dataLoaded && !dataLoadFailed">
            <Loader :image-path="imagePath" />
        </div>
        <div v-else-if="dataLoadFailed">
            <Alert>
                <span v-translate>error.getting.data</span>
            </Alert>
        </div>
    </v-container>
</template>

<script>
    import axios from 'axios'
    import Alert from './Alert'
    import Loader from './Loader'
    import ListTimeline from './ListTimeline'
    import Timeline from './Timeline'
    import LocalizedStringService from "../services/localizedStringService";
    import ElementIdService from "@/services/elementIdService";

    const localizedStringService = new LocalizedStringService();
    const elementIdService = new ElementIdService();

    export default {
        components: {Alert, Loader, ListTimeline, Timeline},
        props: {
            configurationData: Object,
            jsonApi: String,
            imagePath: String
        },
        data: function() {
            return {
                rakenneElementit: [],
                ministeriot: [],
                kohdeLinkit: {},
                kohteidenLinkit: {},
                liittyvatKohdeUuids: [],
                valitavoiteTyypit: [],
                dataLoadFailed: false,
                listMode: true,
                kohdeLinkitLoaded: false,
                kohteidenLinkitLoaded: false
            }
        },
        computed: {
            dataLoaded: function() {
                return this.kohdeLinkitLoaded && this.kohteidenLinkitLoaded;
            }
        },
        mounted: function() {
            if (this.isValidConfiguration()) {
                this.loadRakenneElementit();
                this.loadMinisteriot();
                this.loadValitavoiteTyypit();
            }
        },
        updated: function () {
            this.$nextTick(function () {
                // Code that will run only after the
                // entire view has been re-rendered

                // Need to reset hash to make the window scroll to correct position
                let hash = window.location.hash;
                window.location.hash = "";
                window.location.hash = hash;
            });
        },
        methods: {
            isValidConfiguration() {
                return this.configurationData.hallitusUuid && this.configurationData.hallitusUuid.length > 0 &&
                    this.configurationData.rakenneElementtiUuid && this.configurationData.rakenneElementtiUuid.length > 0;
            },
            loadRakenneElementit() {
                axios
                    .get(
                        this.jsonApi + '/hankeikkuna-government-timeline/data/alatasonHoRakenneElementit',
                        {
                            params: {
                                hallitusUuid: this.configurationData.hallitusUuid,
                                ylatasonElementtiUuid: this.configurationData.rakenneElementtiUuid

                                // hallitusUuid: "3b2eddb9-bfd3-40df-b5ef-1b34340ce7ef",
                                // ylatasonElementtiUuid: "2b70f8bf-9889-46a3-8f8c-669c2d03c7a9"
                                // ylatasonElementtiUuid: "74ec517f-d154-4686-95c4-536beeb9fc7d"
                            }
                        }
                    )
                    .then(response => {
                        this.rakenneElementit = response.data;
                        // console.log(response.data);
                        this.getLiittyvatKohdeUuids();

                        if (this.liittyvatKohdeUuids.length > 0) {
                            this.loadKohdeLinkit();
                            this.loadKohteidenLinkit();
                        } else {
                            this.kohdeLinkitLoaded = true;
                            this.kohteidenLinkitLoaded = true;
                        }
                    })
                    .catch(() => this.dataLoadFailed = true)
            },
            loadMinisteriot() {
                axios
                    .get(
                        this.jsonApi + '/hankeikkuna-government-timeline/data/ministeriot'
                    )
                    .then(response => {
                        this.ministeriot = response.data;
                        // console.log(response.data);
                    })
                    .catch(() => this.dataLoadFailed = true)
            },
            loadKohdeLinkit() {
                axios
                    .post(
                        this.jsonApi + '/hankeikkuna-government-timeline/data/kohdeLinkit?language=' + this.$translate.current,
                        this.liittyvatKohdeUuids
                    )
                    .then(response => {
                        this.kohdeLinkit = response.data;
                        this.kohdeLinkitLoaded = true;
                        // console.log(response.data);
                    })
                    .catch(() => this.dataLoadFailed = true)
            },
            loadKohteidenLinkit() {
                axios
                    .post(
                        this.jsonApi + '/hankeikkuna-government-timeline/data/kohteidenLinkit',
                        this.liittyvatKohdeUuids
                    )
                    .then(response => {
                        this.kohteidenLinkit = response.data;
                        this.kohteidenLinkitLoaded = true
                    })
                    .catch(() => this.dataLoadFailed = true)
            },
            loadValitavoiteTyypit() {
                axios
                    .get(
                        this.jsonApi + '/hankeikkuna-government-timeline/data/hoValitavoiteTyypit'
                    )
                    .then(response => {
                        this.valitavoiteTyypit = response.data;
                        // console.log(response.data);
                    })
                    .catch(() => this.dataLoadFailed = true)
            },
            getLiittyvatKohdeUuids() {
                for (let i = 0 ; this.rakenneElementit.length > i ; i++) {
                    let keinot = this.rakenneElementit[i].valitavoitteet;

                    if (keinot.length > 0) {
                        for (let a = 0 ; keinot.length > a ; a++) {
                            let keino = this.rakenneElementit[i].valitavoitteet[a];

                            if (keino.liittyvatKohdeUuids.length > 0) {
                                for (let b = 0 ; keino.liittyvatKohdeUuids.length > b ; b++) {
                                    let liittyvaKohdeUuid = this.rakenneElementit[i].valitavoitteet[a].liittyvatKohdeUuids[b];
                                    this.liittyvatKohdeUuids.push(liittyvaKohdeUuid);
                                }
                            }
                        }
                    }
                }
            },
            localizedStringValue(localizedString) {
                return localizedStringService.getValue(localizedString, this.$translate.current);
            },
            toggleCollapse() {
                let collapsibleTrigger = event.target;
                let collapsibleContent = event.target.parentNode.nextSibling;

                if (collapsibleContent.style.display === "none") {
                    collapsibleTrigger.setAttribute("aria-expanded", "true");
                    collapsibleTrigger.setAttribute("aria-label", this.$translate.text("close.menu"));
                    collapsibleTrigger.classList.remove('closed');
                    collapsibleTrigger.classList.add('opened');

                    collapsibleContent.style.display = "block";
                    collapsibleContent.setAttribute("aria-hidden", "false");
                } else {
                    collapsibleTrigger.setAttribute("aria-expanded", "false");
                    collapsibleTrigger.setAttribute("aria-label", this.$translate.text("open.menu"));
                    collapsibleTrigger.classList.remove('opened');
                    collapsibleTrigger.classList.add('closed');

                    collapsibleContent.style.display = "none";
                    collapsibleContent.setAttribute("aria-hidden", "true");
                }
            },
            generateIdFromElementti(elementti) {
              let name = this.localizedStringValue(elementti.nimi);
              return elementIdService.generateIdFromName(name);
            }
        }
    }
</script>

<style lang="scss">
    .aui .portlet-hankeikkuna-government-timeline, .portlet-hankeikkuna-government-timeline {

        $black : #222;
        $lightgray : #f0f0f0;
        $gray : #979797;
        $lightblue : #DFE7F5;
        $blue : #365abd;

        .ho-collapsible {
            border: 1px solid $gray;

            .heading {
                margin: 0;

                .ho-collapsible-trigger {
                    position: relative;
                    text-align: left;
                    border-bottom: 1px solid $gray;
                    display: block;
                    width: 100%;
                    padding: 15px 55px 15px 15px;
                    background: $lightblue;
                    box-shadow: none;
                    border-radius: 0;
                    margin: 0;
                    font-weight: inherit !important;
                    font-family: inherit !important;
                    font-size: inherit !important;

                    .trigger-icon {
                        color: $black;
                        pointer-events: none;
                        position: absolute;
                        top: 50%;
                        transform: translateY(-50%);
                        right: 20px;
                        font-size: 20px;
                        line-height: 0;
                    }

                    &.opened {
                        .trigger-icon {
                            transform: rotate(180deg) translateY(50%);
                        }
                    }

                    &:not(.opened) {
                        border-bottom: 0;
                    }
                }
            }

            &.child {
                border: 0;
                border-bottom: 1px solid $gray;

                .heading {
                    .ho-collapsible-trigger {
                        color: $blue;
                        background: $lightgray;
                    }
                }

                .ho-collapsible-content {
                    border-top: 0;
                    border-bottom: 0;
                }

                &:only-child {
                    border: 1px solid $gray;

                    .ho-collapsible-content {
                        .objectiveList {
                            border-bottom: 1px solid $gray;
                        }
                    }
                }
            }

            .structure-description {
                padding: 15px;
                margin: 0;
            }

            .timelineViewToggles {
                margin: 15px 15px 5px 15px;

                button {
                    margin: 0 5px 10px 0;

                    &:first-child {
                        margin-right: 10px;
                    }

                    svg {
                        margin: 0 10px 0 0;
                    }
                }
            }
        }
    }
</style>

